import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
  inject,
} from '@angular/core';
import { GridColumn } from '@shared/modules/grid/abstractions/grid-column';
import { ODataQueryState } from '../../types/odata-query-state';
import { GridRow } from '@shared/models/grid/grid-row';
import { KeyValue } from '@angular/common';
import { PrimeGridFilterFormComponent } from '@shared/modules/prime-grid-filters/components/prime-grid-filter-form/prime-grid-filter-form.component';
import { paginationOptions } from '@shared/constants/pagination-settings';
import { GridQueryBuilderService } from '../../services/grid-query-builder.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { mapToGridRow } from '@shared/mappers/map-to-grid-row';
import { PaginationSetting } from '@grid/abstractions';

@Component({
  standalone: false,
  selector: 'gstar-cards-grid',
  templateUrl: './cards-grid.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardsGridComponent<TData> implements OnInit, OnDestroy {
  private readonly gridQueryBuilder = inject<GridQueryBuilderService<TData>>(
    GridQueryBuilderService
  );
  private readonly destroyRef = inject(DestroyRef);

  headers = input<GridColumn[]>([]);
  source = model<TData[]>([]);
  isLoading = input(true);
  totalRows = input(0);
  sessionKey = input<string | undefined>();
  hasFilters = computed(() => {
    return this.headers().some((header: GridColumn) => header.isFilterable);
  });

  cardTemplate =
    contentChild<TemplateRef<{ $implicit: TData }>>('cardTemplate');

  queryChange = output<ODataQueryState<TData>>();
  dataSource = signal<GridRow<TData>[]>([]);
  options: KeyValue<string, number>[] = paginationOptions;

  filters = viewChild(PrimeGridFilterFormComponent);

  constructor() {
    this.gridQueryBuilder.triggerQuery
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((query: ODataQueryState<TData>) => {
        this.queryChange.emit(query);
      });

    toObservable(this.source)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((source: TData[]) => {
        const gridData = mapToGridRow<TData>(source);
        this.dataSource.set(gridData);
      });

    toObservable(this.totalRows)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((totalRows: number) => {
        this.gridQueryBuilder.updatePagination({ totalItems: totalRows }, true);
      });
  }

  ngOnDestroy(): void {
    if (!this.sessionKey()) {
      this.gridQueryBuilder.reset();
    }
  }

  ngOnInit(): void {
    this.gridQueryBuilder.initialize(this.sessionKey());
  }

  get pagination(): PaginationSetting {
    return this.gridQueryBuilder.pagination();
  }

  clearFilters(): void {
    if (this.filters()) {
      this.filters()?.clearFields();
    }
  }

  clearFiltersAndRun(): void {
    if (this.filters()) {
      this.filters()?.clearAndRun();
    }
  }

  goToPage(currentPage: number): void {
    this.gridQueryBuilder.updatePagination({ currentPage });
  }

  pageChange(pagination: PaginationSetting): void {
    this.gridQueryBuilder.updatePagination(pagination, pagination.isFirst);
  }

  filterChange(filter: ODataQueryState<TData>): void {
    this.gridQueryBuilder.updatePagination({ currentPage: 1 }, true);
    this.gridQueryBuilder.updateFilter(filter);
  }
}
